/**
 * Toast — re-exports Sonner's Toaster with Hostesa brand styling
 * and a typed `toast` helper.
 */
import { Toaster as SonnerToaster, toast as sonnerToast } from 'sonner';

export type ToastType = 'success' | 'error' | 'warning' | 'info' | 'message';

export interface ToastOptions {
  description?: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

/** Typed wrapper around Sonner's toast function. */
export const toast = {
  success: (message: string, opts?: ToastOptions) => sonnerToast.success(message, opts),
  error: (message: string, opts?: ToastOptions) => sonnerToast.error(message, opts),
  warning: (message: string, opts?: ToastOptions) => sonnerToast.warning(message, opts),
  info: (message: string, opts?: ToastOptions) => sonnerToast.info(message, opts),
  message: (message: string, opts?: ToastOptions) => sonnerToast(message, opts),
  dismiss: (id?: string | number) => sonnerToast.dismiss(id),
} as const;

/** Drop-in Toaster component pre-styled for Hostesa brand. */
export function Toaster() {
  return (
    <SonnerToaster
      position="bottom-right"
      toastOptions={{
        classNames: {
          toast: [
            'font-[var(--font-text)] text-sm',
            'bg-[var(--color-alabaster)] text-[var(--color-charcoal)]',
            'border border-[var(--color-border)]',
            'shadow-md rounded-lg',
          ].join(' '),
          title: 'font-medium text-[var(--color-charcoal)]',
          description: 'text-[var(--color-stone-mid)]',
          actionButton: [
            'font-medium text-xs',
            'bg-[var(--color-charcoal)] text-[var(--color-alabaster)]',
            'rounded-md px-2.5 py-1',
          ].join(' '),
          cancelButton: [
            'font-medium text-xs',
            'bg-[var(--color-canvas)] text-[var(--color-stone-mid)]',
            'rounded-md px-2.5 py-1',
          ].join(' '),
          closeButton: ['text-[var(--color-stone-mid)]', 'hover:text-[var(--color-charcoal)]'].join(
            ' ',
          ),
          success: 'border-[var(--color-success)]/30',
          error: 'border-[var(--color-alert)]/30',
          warning: 'border-[var(--color-warm-stone)]/30',
          info: 'border-[var(--color-warm-stone-soft)]',
        },
      }}
    />
  );
}
